export type SupportedPricingCurrency = "GHS" | "NGN" | "KES" | "ZAR" | "GBP" | "USD" | "EUR";

type PublicMarket = {
  currency: SupportedPricingCurrency;
  regionCode: string | null;
  regionLabel: string | null;
};

const EURO_COUNTRIES = new Set([
  "AT", "BE", "CY", "DE", "EE", "ES", "FI", "FR", "GR", "HR",
  "IE", "IT", "LT", "LU", "LV", "MT", "NL", "PT", "SI", "SK",
]);

const COUNTRY_TO_CURRENCY: Record<string, SupportedPricingCurrency> = {
  GH: "GHS",
  NG: "NGN",
  KE: "KES",
  ZA: "ZAR",
  GB: "GBP",
  US: "USD",
  CA: "USD",
};

const COUNTRY_TO_LABEL: Record<string, string> = {
  GH: "Ghana",
  NG: "Nigeria",
  KE: "Kenya",
  ZA: "South Africa",
  GB: "the UK",
  US: "the USA",
  CA: "North America",
};

const TIMEZONE_TO_COUNTRY_PREFIX: Array<[string, string]> = [
  ["Africa/Accra", "GH"],
  ["Africa/Lagos", "NG"],
  ["Africa/Nairobi", "KE"],
  ["Africa/Johannesburg", "ZA"],
  ["Europe/London", "GB"],
  ["America/", "US"],
];

function extractRegionFromLocale(locale: string): string | null {
  if (!locale) return null;
  try {
    // Modern browsers expose region cleanly through Intl.Locale.
    // Fallback regex below handles older environments.
    const region = typeof Intl !== "undefined" && "Locale" in Intl
      ? new Intl.Locale(locale).region
      : null;
    if (region) return region.toUpperCase();
  } catch {
    // Fall through to regex parsing.
  }

  const match = locale.match(/[-_](\w{2})$/);
  return match?.[1] ? match[1].toUpperCase() : null;
}

function detectRegionCode(): string | null {
  if (typeof window === "undefined") return null;

  const locales = Array.isArray(navigator.languages) && navigator.languages.length > 0
    ? navigator.languages
    : [navigator.language];

  for (const locale of locales) {
    const region = extractRegionFromLocale(locale || "");
    if (region) return region;
  }

  const timezone = typeof Intl !== "undefined"
    ? Intl.DateTimeFormat().resolvedOptions().timeZone || ""
    : "";

  for (const [prefix, country] of TIMEZONE_TO_COUNTRY_PREFIX) {
    if (timezone.startsWith(prefix)) return country;
  }

  return null;
}

export function detectPublicMarket(): PublicMarket {
  const regionCode = detectRegionCode();

  if (!regionCode) {
    return { currency: "GHS", regionCode: null, regionLabel: null };
  }

  if (EURO_COUNTRIES.has(regionCode)) {
    return { currency: "EUR", regionCode, regionLabel: "Europe" };
  }

  return {
    currency: COUNTRY_TO_CURRENCY[regionCode] || "GHS",
    regionCode,
    regionLabel: COUNTRY_TO_LABEL[regionCode] || null,
  };
}

export function getStoredPricingCurrency(): SupportedPricingCurrency | null {
  if (typeof window === "undefined") return null;
  const stored = window.localStorage.getItem("buukd:pricing-currency") || "";
  const upper = stored.toUpperCase();
  return ["GHS", "NGN", "KES", "ZAR", "GBP", "USD", "EUR"].includes(upper)
    ? (upper as SupportedPricingCurrency)
    : null;
}

export function storePricingCurrency(currency: string) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem("buukd:pricing-currency", currency.toUpperCase());
}
